/* eslint-disable */

import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Card, CardBody, Tabs, Tab, Text } from '@patternfly/react-core';
import {
	faTable,
	faDisplayChartUpCircleCurrency,
	faNewspaper,
	faPresentation,
	faTableList,
	faChartSimpleHorizontal,
} from '@fortawesome/pro-regular-svg-icons';
import { CardProps } from '../types/cards/card-props';
import ListCards from '../components/card/ListCards';
import { Favorites, TFavorites } from '../api/favorites/Favorites';
import { Recent } from '../api/recents/recents';
import { CardTypesEnum } from '../enums/card-types.enum';
import { RecentTypesEnum } from '../enums/recent-types.enum';
import { FavoriteTypes } from '../enums/favorite-types';
import { useApplication, useIsGranted, useUser } from '../components/user/ApplicationProvider';
import { Permission } from '../enums/permission.enum';
import { User } from '../api/security/User';
import {
	sortByFavoriteThenRecentThenTimestamp,
	sortByTimestamp,
} from '../helpers/helper-components/recents-factory-helper';
import AlertCardsHomePage from '../helpers/helper-components/alert-cards-home-page';
import PageTitleSubheader from '../layout/subheader/PageTitleSubheader';
import { useOutletContext } from 'react-router-dom';
import MainSubNavContext from '../helpers/sub-nav-contexts/main-sub-nav-context';
import { SubNavContext } from '../helpers/sub-nav-contexts/sub-nav-context';
import { OutletContext } from '../layout/Layout';
import { Present } from '../api/present/Present';
import { Report } from '../api/reports/Reports';
import { Dataframe } from '../api/dataframes/Dataframes';
import { Chart } from '../api/charts/Chart';
import { Dashboard } from '../api/dashbboards/Dashboards';
import Loader from '../components/util/Loader';
import { ChartIconMap } from '../helpers/helper-components/chart-helper';
import { ChartTypesEnum } from '../enums/chart-types.enum';
import { useMount } from 'react-use';
import DiscoverSearch from './discover/DiscoverSearch';
import { TNewDateRange } from '../api/types/TNewDateRange';
import { TQuestion } from '../api/questions/Questions';
import { DiscoverCategory } from '../types/discover';
import { QuestionCategory } from '../api/question-category/QuestionCategory';
import { iconMap } from '../helpers/global-icon-map.helper';
import aiAnalystLogo from '../img/zin-ai-analyst-blue.png';
import { ZoneSettingEnum } from '../api/zone/ZoneSetting';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { getOwnerId } from '../helpers/owner.helper';

// Card ids come from six different tables, each with its own sequence, so a bare id is
// not unique across types: dashboard #7 and chart #7 are different cards. Always
// compare on (type, id).
const cardKey = (item: { type: CardTypesEnum; id?: number }): string => `${item.type}:${item.id}`;

// Sources the All tab draws "Others" from when favorites and recents do not fill it,
// each paired with the permission its endpoint requires. Any 403 in the app redirects
// to /restricted-access, so a type the user cannot view must not be requested at all.
const BACKFILL_SOURCES: { type: string; permission: Permission }[] = [
	{ type: CardTypesEnum.Dashboard, permission: Permission.ViewDashboard },
	{ type: CardTypesEnum.Chart, permission: Permission.ViewChart },
	{ type: CardTypesEnum.Table, permission: Permission.ViewTable },
	{ type: CardTypesEnum.DataFrame, permission: Permission.ViewDataframe },
	{ type: CardTypesEnum.Report, permission: Permission.ViewReport },
	{ type: CardTypesEnum.Presentation, permission: Permission.ViewPresentation },
];

function GettingStarted(): ReactElement {
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [filteredItems, setFilteredItems] = useState<CardProps[]>([]);
	const [combinedItems, setCombinedItems] = useState<CardProps[]>([]);
	const [recentItems, setRecentItems] = useState<CardProps[]>([]);
	const [favoriteItems, setFavoriteItems] = useState<CardProps[]>([]);
	const [activeTabKey, setActiveTabKey] = useState<string>('all');
	const currentUser = useUser();
	const isGranted = useIsGranted();
	const hasViewRecentPerm = isGranted(Permission.ViewRecent);
	const hasViewFavoritePerm = isGranted(Permission.ViewFavorite);
	const [subNavContext, setSubNavContext] = useState<SubNavContext>(MainSubNavContext);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [hasLoadedUserItems, setHasLoadedUserItems] = useState<boolean>(false);
	const activeTabKeyRef = useRef<string>('all');

	const [searchTerm, setSearchTerm] = useState<string>('');
	const [categories, setCategories] = useState<DiscoverCategory[]>();
	const [hasLoadedCategories, setHasLoadedCategories] = useState<boolean>(false);
	const [selectedDate, setSelectedDate] = useState<TNewDateRange>();
	const [allQuestions, setAllQuestions] = useState<TQuestion[]>();
	const isOnSearchRoute = window.location.pathname === '/discover/search';
	const [inlineCategoryId, setInlineCategoryId] = useState<number | undefined>(undefined);
	const { zoneSettings, user } = useApplication();
	const [aiAnalystEnabled, setaiAnalystEnabled] = useState(false);

	// Open a category inline; if the same category is clicked again, force a replace
	const openCategory = (id: number) => {
		if (inlineCategoryId === id) {
			// unset then set to force re-render/reload
			setInlineCategoryId(undefined);
			setTimeout(() => setInlineCategoryId(id), 0);
		} else {
			setInlineCategoryId(id);
		}
	};

	useMount(() => {
		Favorites.GetByType(FavoriteTypes.chart).then((response) => {
			const pinnedChart = response.find((x) => x.showOnMain);

			if (pinnedChart) {
				setSubNavContext({
					...subNavContext,
					reportSnapshot: {
						snapshotText: 'From your Favorites',
						title: pinnedChart.name,
						chartId: pinnedChart.entity_id,
					},
				});
			} else {
				setSubNavContext({
					...subNavContext,
					reportSnapshot: {
						snapshotText: '',
						title: '',
						chartId: undefined,
					},
				});
			}
		});

		const categoryQuestions: TQuestion[] = [];

		void QuestionCategory.GetPermitted(['questions']).then((response) => {
			const discoverCategories: DiscoverCategory[] = [];

			response.map((item) => {
				const newCategory: DiscoverCategory = {
					icon: iconMap[item.icon],
					id: item.id!,
					key: `Category_${item.title}`,
					name: item.title,
					sequence: item.sequence ?? 0,
					questions: [],
				};
				discoverCategories.push(newCategory);

				const questions: TQuestion[] = (
					(item.questions as TQuestion[]) ?? ([] as TQuestion[])
				).map((q: TQuestion) => ({
					chart: q.chart,
					description: q.description,
					name: q.name,
					sequence: q.sequence,
					category: newCategory.id,
				}));

				questions.map((q) => {
					categoryQuestions.push(q);
				});
			});

			setCategories(
				discoverCategories.sort((a, b) => {
					const seqDiff = (a.sequence ?? 0) - (b.sequence ?? 0);
					if (seqDiff !== 0) return seqDiff;
					return a.name.localeCompare(b.name);
				})
			);
			setAllQuestions(categoryQuestions);
			setHasLoadedCategories(true);
		});

		const AiAnalystSetting = zoneSettings.find(
			(x) => x.zone_setting_enum_id == ZoneSettingEnum.ai_analyst_enabled
		);

		const hasPermission = User.HasPermission(user, Permission.CanUseAIAnalyst);

		if (AiAnalystSetting && AiAnalystSetting.enabled && hasPermission) {
			setaiAnalystEnabled(true);
		}
	});

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle=""
					pageDescription=""
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
					isCollapsable
					subNavContext={subNavContext}
				/>
			),
			sidebarContext: null,
			additionalClassNames: 'home-page-subbar no-title',
			hideLeftSideBar: true,
		});
	}, [setSubSide, subNavExpanded, setSubNavExpanded, subNavContext]);

	useEffect(() => {
		// Nothing to fetch: clear the loader rather than leaving the panel on a
		// spinner that nothing will ever resolve.
		if (!hasViewRecentPerm && !hasViewFavoritePerm) {
			setIsLoading(false);
			setHasLoadedUserItems(true);

			return;
		}

		setIsLoading(true);

		// Clear only once every started fetch has settled, so the panel does not
		// render half its data and reflow when the first one lands.
		let pending = (hasViewRecentPerm ? 1 : 0) + (hasViewFavoritePerm ? 1 : 0);
		const settle = () => {
			pending -= 1;

			if (pending <= 0) {
				setIsLoading(false);
				setHasLoadedUserItems(true);
			}
		};

		if (hasViewRecentPerm) {
			fetchRecentItems().finally(settle);
		}
		if (hasViewFavoritePerm) {
			fetchFavoriteItems().finally(settle);
		}
	}, [hasViewRecentPerm, hasViewFavoritePerm]);

	// Fetch recent items
	const fetchRecentItems = () => {
		const promises = [
			Recent.GetAll(RecentTypesEnum.Dataframe),
			Recent.GetAll(RecentTypesEnum.Charts),
			Recent.GetAll(RecentTypesEnum.Dashboard),
			Recent.GetAll(RecentTypesEnum.Report),
			Recent.GetAll(RecentTypesEnum.Presentation),
			Recent.GetAll(RecentTypesEnum.Report, true),
		];

		return Promise.all(promises)
			.then(([dataframes, charts, dashboards, reports, presentations, tables]) => {
				const recentDataframeCards = dataframes.map((item) => ({
					icon: faTable,
					type: CardTypesEnum.DataFrame,
					url: `/analyze/${CardTypesEnum.DataFrame}/view/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isRecent: true,
				}));

				const recentChartCards = charts.map((item) => ({
					icon: item.chart_type
						? ChartIconMap[item.chart_type as ChartTypesEnum]
						: faChartSimpleHorizontal,
					type: CardTypesEnum.Chart,
					url: `/analyze/${CardTypesEnum.Chart}/view/chart/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isRecent: true,
				}));

				const recentDashboardCards = dashboards.map((item) => ({
					icon: faDisplayChartUpCircleCurrency,
					type: CardTypesEnum.Dashboard,
					url: `/analyze/${CardTypesEnum.Dashboard}/view/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isRecent: true,
				}));

				const recentReportCards = reports.map((item) => ({
					icon: faNewspaper,
					type: CardTypesEnum.Report,
					url: `/report/view/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isRecent: true,
				}));

				const recentPresentationCards = presentations.map((item) => ({
					icon: faPresentation,
					type: CardTypesEnum.Presentation,
					url: `/present/view/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isRecent: true,
				}));

				const recentTablesCards = tables
					.filter((x) => x.entity_type === RecentTypesEnum.Report)
					.map((item) => ({
						icon: faTableList,
						type: CardTypesEnum.Table,
						url: `/table/view/${item.entity_id}`,
						title: item.name ?? '',
						timestamp: item.accessed_at,
						id: item.entity_id,
						ownerId: getOwnerId(item.owner),
						isRecent: true,
					}));

				const allRecentItems = [
					...recentDataframeCards,
					...recentChartCards,
					...recentDashboardCards,
					...recentReportCards,
					...recentPresentationCards,
					...recentTablesCards,
				];

				setRecentItems(sortByTimestamp(allRecentItems));
			})
			.catch((error) => console.error('Error fetching recent data:', error));
	};

	// Fetch favorite items
	const fetchFavoriteItems = () => {
		const favoritePromises = [
			Favorites.GetByType(FavoriteTypes.dataframe),
			Favorites.GetByType(FavoriteTypes.chart),
			Favorites.GetByType(FavoriteTypes.report),
			Favorites.GetByType(FavoriteTypes.dashboard),
			Favorites.GetByType(FavoriteTypes.table),
			Favorites.GetByType(FavoriteTypes.presentation),
		];

		return Promise.all(favoritePromises)
			.then(([dataframes, charts, reports, dashboards, tables, presentations]) => {
				const favoriteDataframeCards = dataframes.map((item: TFavorites) => ({
					icon: faTable,
					type: CardTypesEnum.DataFrame,
					url: `/analyze/${CardTypesEnum.DataFrame}/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoriteChartCards = charts.map((item: TFavorites) => ({
					icon: item.chartType
						? ChartIconMap[item.chartType as ChartTypesEnum]
						: faChartSimpleHorizontal,
					type: CardTypesEnum.Chart,
					url: `/analyze/${CardTypesEnum.Chart}/view/chart/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoriteDashboardCards = dashboards.map((item: TFavorites) => ({
					icon: faDisplayChartUpCircleCurrency,
					type: CardTypesEnum.Dashboard,
					url: `/analyze/${CardTypesEnum.Dashboard}/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoriteReportCards = reports.map((item: TFavorites) => ({
					icon: faNewspaper,
					type: CardTypesEnum.Report,
					url: `/report/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoriteTableCards = tables.map((item: TFavorites) => ({
					icon: faTableList,
					type: CardTypesEnum.Table,
					url: `/table/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoritePresentationCards = presentations.map((item: TFavorites) => ({
					icon: faPresentation,
					type: CardTypesEnum.Presentation,
					url: `/present/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					ownerId: getOwnerId(item.owner),
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				setFavoriteItems([
					...favoriteDataframeCards,
					...favoriteChartCards,
					...favoriteDashboardCards,
					...favoriteReportCards,
					...favoriteTableCards,
					...favoritePresentationCards,
				]);
			})
			.catch((error) => console.error('Error fetching favorite data:', error));
	};

	useEffect(() => {
		const combined = [
			// All favorites first
			...favoriteItems.map((item) => ({
				...item,
				isFavorite: true,
				isRecent: false,
				tooltip: 'Favorite',
			})),

			// Then only recents that aren't in favorites
			...recentItems
				.filter((recent) => !favoriteItems.some((fav) => cardKey(fav) === cardKey(recent)))
				.map((item) => ({
					...item,
					isFavorite: false,
					isRecent: true,
					tooltip: 'Recently Viewed',
				})),
		];

		setCombinedItems(sortByFavoriteThenRecentThenTimestamp(combined));
	}, [favoriteItems, recentItems]);

	// Single writer of filteredItems for the initial render. Backfills the active tab
	// with Others once the user's own favorites and recents are known, so a user with
	// few or none of their own does not land on an empty panel (ZAP-1878).
	useEffect(() => {
		if (!hasLoadedUserItems) {
			return;
		}

		let cancelled = false;

		setIsLoading(true);
		void populateData(activeTabKeyRef.current).then((items) => {
			if (cancelled) {
				return;
			}

			setFilteredItems(items.slice(0, 18));
			setIsLoading(false);
		});

		return () => {
			cancelled = true;
		};
	}, [combinedItems, hasLoadedUserItems]);

	// Fetches the accessible items of one type. Used to backfill a tab with "Others"
	// when the user's own favorites and recents do not fill the panel.
	const fetchCardsForType = async (type: string): Promise<CardProps[]> => {
		switch (type) {
			case CardTypesEnum.Dashboard:
				return ((await Dashboard.GetAll()) as any[]).map((item) => ({
					icon: faDisplayChartUpCircleCurrency,
					type: CardTypesEnum.Dashboard,
					url: `/analyze/${CardTypesEnum.Dashboard}/view/${item.id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.id,
				}));

			case CardTypesEnum.Chart:
				return ((await Chart.GetAll()) as any[]).map((item) => ({
					icon: item.chart_type
						? ChartIconMap[item.chart_type as ChartTypesEnum]
						: faChartSimpleHorizontal,
					type: CardTypesEnum.Chart,
					url: `/analyze/${CardTypesEnum.Chart}/view/chart/${item.id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.id,
				}));

			case CardTypesEnum.Table:
				return ((await Report.GetAll([], true)) as any[])
					.filter((x) => x.entity_type === RecentTypesEnum.Report)
					.map((item) => ({
						icon: faTableList,
						type: CardTypesEnum.Table,
						url: `/table/view/${item.id}`,
						title: item.name ?? '',
						timestamp: item.accessed_at,
						id: item.id,
					}));

			case CardTypesEnum.DataFrame:
				return ((await Dataframe.GetAll()) as any[]).map((item) => ({
					icon: faTable,
					type: CardTypesEnum.DataFrame,
					url: `/analyze/${CardTypesEnum.DataFrame}/view/${item.id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.id,
				}));

			case CardTypesEnum.Report:
				return ((await Report.GetAll([], false)) as any[]).map((item) => ({
					icon: faNewspaper,
					type: CardTypesEnum.Report,
					url: `/report/view/${item.id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.id,
				}));

			case CardTypesEnum.Presentation:
				return ((await Present.GetAll()) as any[]).map((item) => ({
					icon: faPresentation,
					type: CardTypesEnum.Presentation,
					url: `/present/view/${item.id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.id,
				}));

			default:
				return [];
		}
	};

	const populateData = async (tabKey: string) => {
		// Step 1: Filter current combined items
		let filtered =
			tabKey === 'all' ? combinedItems : combinedItems.filter((item) => item.type === tabKey);

		// Step 2: Only fetch if we need more
		if (filtered.length < 18) {
			try {
				let fetchedItems: CardProps[] = [];

				if (tabKey === 'all') {
					// The All tab backfills across every type, alphabetically, matching the
					// "Favorites, Recents, Others" sequence in ZAP-1878.
					// Only the types this user may actually view, so the backfill cannot
					// trigger the global 403 redirect. allSettled on top of that, so a
					// source failing for any other reason costs only its own cards.
					const perType = await Promise.allSettled(
						BACKFILL_SOURCES.filter((source) => isGranted(source.permission)).map(
							(source) => fetchCardsForType(source.type)
						)
					);

					fetchedItems = perType
						.flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
						.sort((a, b) => a.title.localeCompare(b.title));
				} else {
					fetchedItems = await fetchCardsForType(tabKey);
				}

				// Step 3: Combine — avoid duplicates by (type, id), and cap at 18
				const uniqueExistingKeys = new Set(filtered.map(cardKey));
				const newUniqueItems = fetchedItems.filter(
					(f) => !uniqueExistingKeys.has(cardKey(f))
				);

				const totalNeeded = 18 - filtered.length;
				const itemsToAdd = newUniqueItems.slice(0, totalNeeded);

				filtered = [...filtered, ...itemsToAdd].slice(0, 18);
			} catch (error) {
				console.error(`Error fetching data for tab ${tabKey}:`, error);
			}
		} else if (tabKey == 'all') {
			filtered = combinedItems.slice(0, 18);
		}

		return filtered;
	};

	const handleTabSelect = async (_: any, tabKey: string | number) => {
		setActiveTabKey(tabKey as string);
		activeTabKeyRef.current = tabKey as string;
		setIsLoading(true);
		const filtered = await populateData(tabKey.toString());
		setIsLoading(false);
		// Step 4: Update the state
		setFilteredItems(filtered.slice(0, 18));
	};

	return (
		<>
			{aiAnalystEnabled && (
				<>
					<Card>
						<CardBody style={{ position: 'relative' }}>
							<div style={{ position: 'relative', zIndex: 1 }}>
								<DiscoverSearch
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									aiSearchEnabled={true}
									categories={categories}
									allQuestions={allQuestions}
									hasLoadedCategories={hasLoadedCategories}
									openCategory={openCategory}
									setSelectedDate={setSelectedDate}
									isOnSearchRoute={isOnSearchRoute}
								/>
							</div>
						</CardBody>
					</Card>
					<br />
				</>
			)}
			<div>
				<Card className="combined-items-card-panel">
					<CardBody>
						{isLoading && <Loader />}
						{!isLoading && (
							<Tabs
								activeKey={activeTabKey}
								onSelect={(event, key) => {
									setActiveTabKey(key as string);
									handleTabSelect(event, key);
								}}
								className="parent-tabs"
							>
								<Tab
									eventKey="all"
									title="All"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Dashboard.toLowerCase()}
									title="Dashboards"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Chart.toLowerCase()}
									title="Charts"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Table.toLowerCase()}
									title="Tables"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.DataFrame.toLowerCase()}
									title="Dataframes"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Report.toLowerCase()}
									title="Reports"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Presentation.toLowerCase()}
									title="Presentations"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
							</Tabs>
						)}
					</CardBody>
				</Card>

				{/* Alerts Section */}
				<br />
				<Card>
					<CardBody>
						<Text className="font-size-md">
							<strong>Alerts</strong>
						</Text>
						<br />
						<AlertCardsHomePage />
					</CardBody>
				</Card>
			</div>
		</>
	);
}

export default GettingStarted;
